import Vue from "vue";
import moment from "moment";
import { required, requiredIf } from "vuelidate/lib/validators";
import { nanoid, customAlphabet } from 'nanoid'
import lodash from 'lodash';
import deepdash from 'deepdash';
import Hashids from 'hashids';
import numeral from "numeral";
import webAppConfig from "./webApp.config";
import { urlValidator, htmlValidator, emailValidator, phoneValidator, uNCValidator, commentMentionsValidator } from "./validator";
import { lightenPalette } from "./colors";
import { builderNotAvailableMessage } from "./message";
import { DeviceName } from "./enum";
import LocalStorageService from "../store/LocalStorageService";
/* assets */
import defaultProfileImage from "../../assets/images/default/default-profile.png";
import defaultImage from "../../assets/images/default/default-image.png";
import fetiasLogo from "../../assets/images/logo/inistate-logo.png";
import twemojiSprite from "../../assets/twemoji/emoji.svg";

const _ = deepdash(lodash);
const hashids = new Hashids('', 10);
const moduleHashids = new Hashids('', 5);
const crypto = require('crypto');

moment.updateLocale("en", {
  relativeTime: {
    future: "%s",
    past: "%s",
    s: 'few sec',
    ss: '%d sec',
    m: "1 min",
    mm: "%d min",
    h: "1 hr",
    hh: "%d hr",
    d: "1 d",
    dd: "%d d",
    w: "1 wk",
    ww: "%d wk",
    M: "1 mo",
    MM: "%d mo",
    y: "1 yr",
    yy: "%d yr"
  }
});

const iOSAppStoreURL = "https://apps.apple.com/app/fetias/id1448325160";
const androindPlayStoreURL = "https://play.google.com/store/apps/details?id=com.gneysoftware.fetias_mobile";
const websiteURL = "https://inistate.com/home";
const termsURL = "https://inistate.com/terms";
const privacyPolicyURL = "https://inistate.com/privacyPolicy";
const communityURL = "https://community.inistate.com";
const youtubeURL = "https://www.youtube.com/channel/UCADsnXVnVkOBhR-haVDo65w";
const facebookURL = "https://www.facebook.com/inistatehq";
const twitterURL = "https://twitter.com/inistatehq";
const linkedinURL = "https://sg.linkedin.com/company/inistate";
const discordURL = "https://discord.gg/tyYjS9mC9k";
const blogURL = "https://blog.inistate.com/";

/* information types */
const informationTypes = [
  { id: 0, type: 'Text', type: 'Text', input: 'text', icon: 'fts-icon-text', iconL: 'text', size: 2, searchable: true, },
  { id: 16, type: 'Multiline Text', input: 'textArea', icon: 'fts-icon-text-area', iconL: 'multiline-text', size: 2, searchable: true },
  { id: 1, type: 'Yes No', input: 'boolean', icon: 'fts-icon-check-square', iconL: 'boolean', size: 2, searchable: true },
  { id: 2, type: 'Integer', input: 'integer', icon: 'fts-icon-integer', iconL: 'integer', size: 2, searchable: true },
  { id: 3, type: 'Number', input: 'number', icon: 'fts-icon-number', iconL: 'number', size: 2, searchable: true },
  { id: 4, type: 'Money', input: 'number', icon: 'fts-icon-dollar-sign', iconL: 'currency', size: 2, searchable: true },
  { id: 5, type: 'Date', input: 'date', icon: 'fts-icon-calendar', iconL: 'date', size: 2, searchable: true },
  { id: 6, type: 'Date Time', input: 'dateTime', icon: 'fts-icon-calendar', iconL: 'datetime', size: 2, searchable: true },
  { id: 7, type: 'Module', input: 'dropDown', icon: 'fts-icon-locus', iconL: 'locus', additionalInformation: true, size: 2, searchable: true },
  { id: 8, type: 'Modules', input: 'multidropDown', icon: 'fts-icon-locus', iconL: 'locus', size: 2, searchable: true },
  { id: 9, type: 'Location', input: 'location', icon: 'fts-icon-map-marker', iconL: 'location', size: 2, searchable: true },
  { id: 10, type: 'Image', input: 'imageUpload', icon: 'fts-icon-image', iconL: 'image', size: 2 },
  { id: 11, type: 'Images', input: 'imagesUpload', icon: 'fts-icon-images', iconL: 'images', size: 2 },
  { id: 12, type: 'File', input: 'fileUpload', icon: 'fts-icon-file', iconL: 'file', size: 2 },
  { id: 13, type: 'Files', input: 'filesUpload', icon: 'fts-icon-files', iconL: 'files', size: 2 },
  { id: 14, type: 'Table', input: 'table', icon: 'fts-icon-list-table', iconL: 'table', searchable: true, additionalInformation: true, size: 1 },
  { id: 15, type: 'List (Alpha)', input: 'list', icon: 'fts-icon-list-table', iconL: 'table', additionalInformation: true, size: 1, isAlpha: true },
  //Action,
  //Hidden,
  //Username,
  { id: 19, type: 'Username', input: 'username', icon: 'fts-icon-user', iconL: 'user', size: 2, searchable: true, internal: true },
  { id: 20, type: 'User', input: 'dropDown', icon: 'fts-icon-user', iconL: 'user', additionalInformation: true, size: 2, searchable: true },
  { id: 21, type: 'Users', input: 'multidropDown', icon: 'fts-icon-users', iconL: 'user', size: 2, searchable: true },
  //Users
  { id: 22, type: 'Content', input: 'content', icon: 'fts-icon-font-underline', iconL: 'content', size: 2 },
  //{ id: 23, type: 'Checklist', input: 'checklist', icon: 'check-list' },
  { id: 24, type: 'Context', input: 'contextSelection', icon: 'fts-icon-check-square', iconL: 'locus', size: 2 },
  { id: 25, type: 'Email', input: 'email', icon: 'fts-icon-envelope', iconL: 'email', size: 2, searchable: true },
  { id: 26, type: 'Phone Number', input: 'phoneNumber', icon: 'fts-icon-phone', iconL: 'phonenumber', size: 2, searchable: true },
  { id: 27, type: 'Select', input: 'dropDown', icon: 'fts-icon-menu-caret-down', iconL: 'select', additionalInformation: true, size: 2, searchable: true },
  { id: 28, type: 'Tag', input: 'dropDown', icon: 'fts-icon-tag', iconL: 'select', additionalInformation: true, size: 2, searchable: true },
  { id: 29, type: 'Signature', input: 'signature', icon: 'fts-icon-signature', iconL: 'signature', size: 2 },
  { id: 30, type: 'Date Range', input: 'dateRange', icon: 'fts-icon-calendar', iconL: 'date', size: 2, internal: true },
  // { id: 31, type: 'DateTimeRange', input: 'dateTimeRange', icon: 'dateTimeRange' },
  { id: 32, type: 'Formula', input: 'formula', icon: 'fts-icon-formula', iconL: 'formula', additionalInformation: true, size: 2, internal: true },
  { id: 33, type: 'URL', input: 'link', icon: 'fts-icon-link', iconL: 'link', size: 2, searchable: true },
  { id: 35, type: 'Form', input: 'form', icon: 'fts-icon-form', internal: true },
  { id: 36, type: 'Table Row', input: 'tableRow', icon: 'fts-icon-table-row', internal: true },
  { id: 37, type: 'Unknown', input: 'unknown', icon: 'fts-icon-unknown', internal: true },
]
/* information types */
const expandInformationTypes = (item, list, references) => {
  list = Array.isArray(list) ? [...new Set(list), item] : [item];
  const res = references.find(x => x.from === item);
  res && res.to.forEach(x => {
    if (!list.some(y => y === x)) {
      list = list.concat(expandInformationTypes(x, [...list, x], references));
    }
    list.push(x)
  });
  return [...new Set(list)];
}
/* information type expand */
const informationTypeReferences = [
  { from: 0, to: [16] },
  { from: 1, to: [0] },
  { from: 2, to: [3, 4] },
  { from: 3, to: [4, 0] },
  { from: 4, to: [0] },
  { from: 5, to: [6] },
  { from: 6, to: [0] },
  { from: 10, to: [12, 13, 11, 33] },
  { from: 11, to: [13] },
  { from: 12, to: [13, 33] },
  { from: 13, to: [0] },
  { from: 16, to: [0] },
  { from: 25, to: [0] },
  { from: 26, to: [0] },
  { from: 27, to: [0] },
  { from: 33, to: [0] },
];
const expandInformationType = (x) => expandInformationTypes(x, [], informationTypeReferences);
/* connector information type expand */
const connectorInformationTypeReferences = [
  { from: 0, to: [] },
  { from: 2, to: [] },
  { from: 5, to: [6] },
  { from: 6, to: [5] },
  { from: 19, to: [0] }
];
const expandConnectorInformationType = (x) => expandInformationTypes(x, [], connectorInformationTypeReferences);
/* standard */
const standards = [
  { id: 'createEditView', name: 'Create / Edit / View' },
  { id: 'create', name: 'Create' },
  { id: 'edit', name: 'Edit' },
  { id: 'view', name: 'View' },
  { id: 'quickView', name: 'Quick View' },
  { id: 'delete', name: 'Delete' },
  { id: 'changeStatus', name: 'Change State' },
  { id: 'duplicate', name: 'Duplicate' },
  { id: 'comment', name: 'Comment' },
  { id: 'history', name: 'History' },
  { id: 'share', name: 'Share' },
  { id: 'assign', name: 'Assign', beta: true },
  { id: 'import', name: 'Import', beta: true },
  { id: 'export', name: 'Export', beta: true },
  { id: 'print', name: 'Print', beta: true },
];
/* standard activities */
const standardActivities = [
  {
    id: "view",
    name: "View",
    icon: "fts-icon-file",
    classification: true,
    standard: true,
    share: true
  },
  {
    id: "create",
    name: "New",
    icon: "fts-icon-edit",
    classification: true,
    standard: true,
    share: true
  },
  {
    id: "edit",
    name: "Edit",
    icon: "fts-icon-edit",
    classification: true,
    quickView: true,
    standard: true,
    share: true
  },
  {
    id: "delete",
    name: "Delete",
    icon: "fts-icon-trash",
    quickView: true,
    bulk: true,
    standard: true
  },
  {
    id: "duplicate",
    name: "Duplicate",
    icon: "fts-icon-duplicate",
    standard: true
  },
  {
    id: "changeStatus",
    name: "Change State",
    icon: "fts-icon-sync",
    quickView: true,
    bulk: true,
    standard: true
  },
  {
    id: "assign",
    name: "Assign",
    icon: "fts-icon-user-plus",
    quickView: true,
    bulk: true,
    standard: true
  }
];

const adminFeatures = [
  {
    id: "manage",
    name: "Manage",
    icon: "fts-icon-pen",
    tooltip: "For admin only",
    standard: true,
  }
];
/* data format sample */
const dateFormatSample = `${moment().year()}-03-27`;
/* date formats */
const dateFormats = [
  { id: 't', name: moment(dateFormatSample).format('LT'), cSharp: 'h:mm tt' },
  { id: 'D', name: moment(dateFormatSample).format('l'), cSharp: 'M/d/yyyy' },
  { id: 'LL/dd/yyyy', name: moment(dateFormatSample).format('L'), cSharp: 'MM/dd/yyyy' },
  { id: 'd/L/yyyy', name: moment(dateFormatSample).format('D/M/YYYY'), cSharp: 'd/M/yyyy' },
  { id: 'dd/LL/yyyy', name: moment(dateFormatSample).format('DD/MM/YYYY'), cSharp: 'dd/MM/yyyy' },
  { id: 'DD', name: moment(dateFormatSample).format('ll'), cSharp: 'MMM dd, yyyy' },
  { id: 'DDD', name: moment(dateFormatSample).format('LL'), cSharp: 'MMMM dd, yyyy' },
  { id: 'DD t', name: moment(dateFormatSample).format('lll'), cSharp: 'MMM dd, yyyy h:mm tt' },
  { id: 'DDD t', name: moment(dateFormatSample).format('LLL'), cSharp: 'MMMM dd, yyyy h:mm tt' },
  { id: 'ccc, LLL d, yyyy t', name: moment(dateFormatSample).format('llll'), cSharp: 'ddd, MMM dd, yyyy h:mm tt' },
  { id: 'cccc, LLLL d, yyyy t', name: moment(dateFormatSample).format('LLLL'), cSharp: 'dddd, MMM dd, yyyy h:mm tt' },
];
/* date format translate */
const dateFormatTranslate = ((x) => {
  let dateFormat = x
  if (dateFormat) {
    dateFormat = dateFormat.replace(/\bt\b/g, 'hh:mm a')
    dateFormat = dateFormat.replace(/\bD\b/g, 'L/d/yyyy')
    dateFormat = dateFormat.replace(/\bDD\b/g, 'LLL d, yyyy')
    dateFormat = dateFormat.replace(/\bDDD\b/g, 'LLLL d, yyyy')
  }
  return dateFormat
})
/* relative time */
let momentRelativeTime = function (date) {
  if (isDateISO_8601(date))
    return moment(date).calendar(null, {
      lastWeek: () => `[${moment(date).fromNow()}]`,
      lastDay: '[yesterday]',
      sameDay: () => `[${moment(date).fromNow()}]`,
      nextDay: '[tomorrow]',
      nextWeek: () => `[${moment(date).fromNow()}]`,
      sameElse: () => `[${moment(date).fromNow()}]`,
    })
}
/* calendar relative time */
let calendarRelativeTime = function (date) {
  const today = moment().startOf('day');
  if (isDateISO_8601(date))
    return moment(date).calendar(null, {
      lastWeek: () => moment(today).isSame(date, 'week') ? `[${moment(date).fromNow()}]` : '[last week]',
      lastDay: '[yesterday]',
      sameDay: '[today]',
      nextDay: '[tomorrow]',
      nextWeek: 'ddd',
      sameElse: () => {
        if (moment(moment(today).subtract(7, 'd')).isSame(date, 'week'))
          return '[last week]';
        else if (moment(moment(today).subtract(7, 'd')).isSame(date, 'week'))
          return 'ddd';
        else
          return `[${moment(date).startOf('day').from(today)}]`;
      }
    });
}
/* format number  */
const formatNumber = (number, { ...option }) => {
  const prefix = option.prefix || '$';
  let format = '0,0.[00]';
  if (option.abbreviate && (Math.log(Math.abs(number) + 1) * 0.43429448190325176 | 0) + 1 > 4) {
    format = `${format}a`;
  }
  if (option.currency) {
    format = `${prefix}${format}`;
  }
  return numeral(number).format(format);
};
/* draggable option */
const dragOptions = {
  animation: 150,
  ghostClass: "ghost",
  forceFallback: true,
}

var newUUID = function () {
  // http://www.ietf.org/rfc/rfc4122.txt

  var s = [];
  var hexDigits = "0123456789abcdef";
  for (var i = 0; i < 36; i++) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = "4"; // bits 12-15 of the time_hi_and_version field to 0010
  s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
  s[8] = s[13] = s[18] = s[23] = "-";

  return s.join("");
}

// Turns a raw AI TeamMate progress frame (tool name + the identifiers the server surfaces —
// module, activity, entryId) into the short, human-readable line shown in the "Thinking…"
// bubble. Kept here so the wording can be tweaked/localized in one place and shared across the
// teammate components; the server deliberately sends identifiers only, never the raw tool input.
const describeTeammateProgress = (p) => {
  if (!p) return null;
  const m = p.module || null;
  switch (p.tool) {
    case "list_entries":
      return m ? `Reading ${m} list` : "Reading entries";
    case "get_entry":
      return m ? `Reading ${m} #${p.entryId}` : "Reading an entry";
    case "get_entry_history":
      return m ? `Reviewing ${m} #${p.entryId} history` : "Reviewing entry history";
    case "get_form": {
      const a = p.activity || null;
      if (!a) return m ? `Opening the ${m} form` : "Opening a form";
      return m ? `Opening the ${a} form on ${m}` : `Opening the ${a} form`;
    }
    case "submit_activity":
    case "propose_activity": {
      const a = p.activity || null;
      return a ? `${a} on ${m}` : `Updating ${m}`;
    }
    case "submit_activities":
      return m ? `Updating several ${m} entries` : "Updating several entries";
    case "list_modules":
      return "Browsing modules";
    case "get_module_schema":
      return m ? `Checking the ${m} setup` : "Checking module setup";
    case "get_module_canvas":
      return m ? `Reviewing the ${m} workflow` : "Reviewing the workflow";
    case "design_module":
      return "Drafting a new module";
    case "modify_module":
      return m ? `Drafting changes to ${m}` : "Drafting module changes";
    default:
      return p.tool || null;
  }
}

const nanoID = (len = 12) => {
  return nanoid(len);
}

const randomInt = (min, max) => {
  const range = max - min + 1;
  const randomBuffer = crypto.randomBytes(4);
  const randomNumber = randomBuffer.readUInt32BE(0);
  return min + (randomNumber % range);
} 

/**
 * Notification badge text (SS06072): the server caps the unread count it reports, and both
 * clients must show the same overflow marker instead of a bare capped number.
 * @param {Number|String} count unread notifications as reported by the server
 * @param {Number} max largest exact count shown; anything above reads as "<max>+"
 * @returns {String} "" when nothing is unread
 */
const formatBadgeCount = (count, max = 99) => {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return "";
  return n > max ? `${max}+` : String(Math.floor(n));
}
const encodeids = (ids) => {
  return isInteger(ids) ? hashids.encode(ids) : ids;
};

const decodeids = (ids) => {
  if (isInteger(ids) || !hashids.isValidId(ids)) {
    return ids;
  }

  const decode = hashids.decode(ids);
  return isArray(decode) && decode.length ? decode[0] : ids;
}
/**
 * 
 * @param {String, Number} moduleId Nullable
 * @param {String, Number} entryId 
 * @returns
 */
const queryEncodeids = (moduleId, entryId) => {
  let mId = moduleId;
  let eId = entryId;
  let mIdEncode = false;
  let eIdEncode = false;
  if (moduleId && isInteger(moduleId)) {
    mId = moduleHashids.encode(moduleId);
    mIdEncode = true;
  }
  if (entryId && isInteger(entryId)) {
    eId = hashids.encode(entryId);
    eIdEncode = true;
  }
  /**
   * a - [mdId, eId] encode moduleId, encode entryId
   * b - [mdId, eId] encode moduleId
   * c - [mdId, eId] encode entryId
   * d - [mdId, eId] moduleId, entryId
   * e - [eId] encode entryId
   * f - [eId] entryId
   */
  const prefixAlphabet = () => {
    if (mIdEncode && eIdEncode) return "a";
    else if (mIdEncode) return "b";
    else if (eIdEncode) return "c";
    else return "d";
  }

  if (mId && eId) {
    return `${prefixAlphabet()}${mId}_${eId}`;
  } else if (eId) {
    return `${eIdEncode ? 'e' : 'f'}${eId}`;
  }
}

const queryDecodeids = (ids) => {
  try {
    const prefixAlphabet = ids.slice(0, 1);
    const decodeModuleId = (id) => {
      const decode = moduleHashids.decode(id);
      return isArray(decode) && decode.length ? decode[0] : id;
    }
    const decodeEntryId = (id) => {
      const decode = hashids.decode(id);
      return isArray(decode) && decode.length ? decode[0] : id;
    }
    /* entryId — the module was deliberately left out (wildcard connections have no fixed
       target module), so `moduleUnspecified` tells callers NOT to substitute the module of the
       route they happen to be on: the entry usually lives somewhere else. The server resolves
       the module from the entry when moduleId is null. See SS05672. */
    if ((prefixAlphabet === 'e' || prefixAlphabet === 'f') && ids.length === 11) {
      const eId = ids.slice(1);
      return {
        entryId: prefixAlphabet === 'e' ? decodeEntryId(eId) : eId,
        moduleUnspecified: true
      };
    }
    /* moduleId + entryId */
    if (ids.includes('_')) {
      let mId = ids.split("_")[0].slice(1);
      let eId = ids.split("_")[1];
      if (mId && eId) {
        if (prefixAlphabet === 'a') {
          mId = decodeModuleId(mId);
          eId = decodeEntryId(eId);
        } else if (prefixAlphabet === 'b') {
          mId = decodeModuleId(mId);
        } else if (prefixAlphabet === 'c') {
          eId = decodeEntryId(eId);
        } else if (prefixAlphabet !== 'd') {
          throw `undefined prefix alphabet ${prefixAlphabet}`;
        }
        return { moduleId: mId, entryId: eId };
      } else {
        throw `Module Id: ${mId}, Entry Id: ${eId}`;
      }
    } else { /* deprecated query id (entryId only) */
      console.warn('deprecated query id');
      return { entryId: decodeids(ids) };
    }
  } catch (err) {
    console.error(err);
    return {};
  }
}

var model = function (data, fieldName, forId) {
  let result = null;
  if (fieldName && fieldName.indexOf(".") > -1) {
    var fields = fieldName.split(".");
    result = data;
    for (let i = 0; i < fields.length; i++) {
      if (forId && i == fields.length - 1)
        result = result[fields[i] + "Id"];
      else result = result[fields[i]];
      if (result == null) {
        result = null;
        break;
      }
    }
  } else {
    if (forId) result = data[fieldName + "Id"];
    else result = data[fieldName];
  }

  return result;
}

let standardActivityIcon = (id => {
  const result = standardActivities.find(x => x.id === id);
  return result && result.icon;
})

let informationIcon = (typeId => {
  switch (typeId) {
    case "state":
      return "fts-icon-state";
    case "collaboration":
      return "fts-icon-users";
    case "password":
      typeId = 0
      break;
    case "toggleSwitch":
      typeId = 1
      break;
  }

  const result = informationTypes.find(x => x.id == typeId);
  return result && result.icon;
})

/* A module reference (type 7 — entry variables, connection fields) renders as the module's own
   user-defined emoji when it has one; the generic locus glyph is the fallback. Twemoji codepoint
   logic mirrors FTSMoji so the raw-HTML chip surfaces (fts-i-unit) draw the same sprite. */
const emojiCodePoints = (unicodeSurrogates) => {
  const vs16RegExp = /\uFE0F/g;
  const zeroWidthJoiner = String.fromCharCode(0x200d);
  const raw = unicodeSurrogates.indexOf(zeroWidthJoiner) < 0
    ? unicodeSurrogates.replace(vs16RegExp, "")
    : unicodeSurrogates;
  const points = [];
  let char = 0;
  let previous = 0;
  let i = 0;
  while (i < raw.length) {
    char = raw.charCodeAt(i++);
    if (previous) {
      points.push((0x10000 + ((previous - 0xd800) << 10) + (char - 0xdc00)).toString(16));
      previous = 0;
    } else if (char > 0xd800 && char <= 0xdbff) {
      previous = char;
    } else {
      points.push(char.toString(16));
    }
  }
  return points.join("-");
};

let moduleEmoji = ((value, modules) => {
  if (!value || value.type != 7 || !value.moduleId || !Array.isArray(modules)) return null;
  const module = modules.find(x => x && x.id == value.moduleId);
  return (module && module.emoji) || null;
});

let informationIconHtml = ((value, modules) => {
  const emoji = moduleEmoji(value, modules);
  if (emoji) {
    return `<svg class="fts-moji information-icon information-icon--moji" width="12" height="12"`
      + ` style="display:inline-block;min-width:12px;vertical-align:-2px;"><use xlink:href="${twemojiSprite}#${emojiCodePoints(emoji)}"></use></svg>`;
  }
  return `<i class="information-icon ${informationIcon(value && value.type)}"></i>`;
});

// Legacy
let getInformationIcon = (typeId => {
  const result = informationTypes.find(x => { return x.id == typeId })
  return result && result.iconL ? result.iconL.toLowerCase() : ''
})

var camelize = function (str) {
  if (str == null)
    return null

  return str.replace(/\W+/g, '').replace(/(?:^\w|[A-Z]|\b\w)/g, function (letter, index) {
    return index == 0 ? letter.toLowerCase() : letter.toUpperCase();
  })
}

var displayName = function (text) {
  if (text) {
    text = text.charAt(0).toUpperCase() + text.slice(1);
    return text.replace(/([A-Z])/g, ' $1').trim();
  }
  return ''
}

var fileColor = function (fileName) {
  if (fileName) {
    if (/\.(pdf)$/i.test(fileName))
      return "var(--fts-file-pdf)";
    else if (/\.(doc|docx)$/i.test(fileName))
      return "var(--fts-file-word)";
    else if (/\.(ppt|pptx)$/i.test(fileName))
      return "var(--fts-file-powerpoint)";
    else if (/\.(xlsx|xls|csv)$/i.test(fileName))
      return "var(--fts-file-excel)";
    else if (/\.(jpeg|jpe|jpg|svg|gif|png|webp|bmp|)$/i.test(fileName))
      return "var(--fts-file-image)";
    else if (/\.(html)$/i.test(fileName))
      return "var(--fts-file-html)";
  }
  return "var(--fts-file-default)";
}

const fileExtension = (fileName) => fileName && fileName.split(".").pop().substring(0, 4);
/**
 * 
 * @param {*} path 
 * @param {*} prefix {moduleId}-{informationId}
 * @returns 
 */
const filePath = (path, prefix) => {
  if (path) {
    const url = path.split('/');
    const filename = encodeURIComponent(url.pop());
    if (path.startsWith('/s/')) {
      const encodeURL = `/api/d/${prefix}/${url.join("/")}/${filename}`;
      return webAppConfig.baseURL ?
        `${webAppConfig.baseURL}${encodeURL}` : encodeURL;
    } else if (path.startsWith('ts/')) {
      const encodeURL = `/api/d/${url.join("/")}/${filename}`;
      return webAppConfig.baseURL ?
        `${webAppConfig.baseURL}${encodeURL}` : encodeURL;
    } else {
      const encodeURL = `${url.join("/")}/${filename}`;
      if (urlValidator(encodeURL)) {
        return path;
      }
      return webAppConfig.baseURL && !path.startsWith('blob:') ?
        `${webAppConfig.baseURL}/${encodeURL}` : encodeURL;
    }
  }
}

const imageTypes = ['jpeg', 'jpg', 'png', 'bmp', 'ico'];

const unsupportedImageTypes = ['jpe', 'gif', 'svg', 'webp', 'tif', 'tiff', 'cur'];

const isImageFile = (val) => /\.(jpeg|jpg|png|bmp|ico|)$/i.test(val);

const isInteger = (val) => /^-?\d+$/.test(val);

const isPossibleNumber = (val) => !isNaN(parseFloat(val));

const isNumber = (val) => /^-?\d*\.{0,1}\d+$/.test(val);

const isArray = (val) => Object.prototype.toString.call(val) === "[object Array]";

const isObject = (val) => Object.prototype.toString.call(val) === "[object Object]";

const isDateISO_8601 = (val) => moment(val, moment.ISO_8601).isValid();

const isMobileDevice = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

var selectOptionBackground = function (payload) {
  const option = payload.option
  const options = payload.optionList || []
  const text = payload.text

  if (option && typeof option === 'object' && option.color)
    return option.color

  let findOption = null
  if (option == null && text != null)
    findOption = options.find(x => x.name == text)

  if (findOption && typeof findOption === 'object' && findOption.color)
    return findOption.color

  const colors = _.difference(lightenPalette, _.map(options, 'color'))

  let skip = 0
  if (findOption) {
    skip += options.filter(x => !x.color).indexOf(findOption)
  } else if (option == null || typeof option === 'string') {
    skip += options.filter(x => !x.color).length + 1
  } else {
    skip += options.filter(x => !x.color).indexOf(option)
  }

  const colorSize = colors.length
  if (skip > colorSize - 1) {
    skip -= colorSize * Math.trunc(skip / colorSize)
  }

  return colors[skip]
}

var taskDueDateShortcuts = [
  {
    id: "today",
    label: "Today",
    startDate: moment()
      .startOf("day")
      .format(),
    endDate: moment()
      .endOf("day")
      .format()
  },
  {
    id: "tomorrow",
    label: "Tomorrow",
    startDate: moment()
      .add(1, "day")
      .startOf("day")
      .format(),
    endDate: moment()
      .add(1, "day")
      .endOf("day")
      .format()
  },
  {
    id: "next3Day",
    label: "Next 3 Day",
    startDate: moment()
      .add(3, "day")
      .startOf("day")
      .format(),
    endDate: moment()
      .add(3, "day")
      .endOf("day")
      .format()
  },
  {
    id: "nextWeek",
    label: "Next Week",
    startDate: moment()
      .add(1, "week")
      .startOf("day")
      .format(),
    endDate: moment()
      .add(1, "week")
      .endOf("day")
      .format()
  },
  {
    id: "next2Week",
    label: "Next 2 Week",
    startDate: moment()
      .add(2, "week")
      .startOf("day")
      .format(),
    endDate: moment()
      .add(2, "week")
      .endOf("day")
      .format()
  },
  {
    id: "nextMonth",
    label: "Next Month",
    startDate: moment()
      .add(1, "month")
      .startOf("day")
      .format(),
    endDate: moment()
      .add(1, "month")
      .endOf("day")
      .format()
  }
];

var getAllElements = function (elements, form) {
  if (elements == null) {
    elements = [];
  }

  if (form && form.rows != null) {
    form.rows.forEach(row => {
      if (row.items != null) {
        row.items.forEach(item => {
          getAllElementsInternal(elements, item);
        });
      }
    })
  }

  return elements;
}

var getAllElementsInternal = function (elements, item) {
  if (item.input == 'section') {
    //tab
    if (item.type == 'tab' && item.tabs) {
      item.tabs.forEach(tab => {
        if (tab.design) {
          getAllElements(elements, tab.design);
        }
      });
    }
    //design
    else if (item.design) {
      getAllElements(elements, item.design);
    }
  } else if (item && item.id) {
    elements.push(item);
  }

  return elements;
}

var generateFieldName = function () {
  const customId = customAlphabet('abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ', 6)
  const id = customId()
  return id[0].toLowerCase() + id.slice(1)
}

let propDowns = [
  /* general */
  'type', 'fieldName', 'displayName', 'formulaMode', 'computed',
  /* number */
  'customFormat', 'decimalPlaces',
  /* date / date time */
  'predefinedFormatId',
  /* select */
  'optionList',
  /* module */
  'connectionFieldId', 'connectionId', 'canAddNew', 'allowOther',
  'displayTemplate', 'valueTemplate',
  /* reference */
  'referenceModuleId', 'referenceModuleTableId'
];

var expandWorkDesignInternal = function (information, item) {
  const elements = ["description", "image", "empty", 'button'];
  /* elements that nothing need to be done */
  if (item && elements.some(x => x === item.input)) {
    if (!item.id) item.id = nanoID()
    return item
  }
  else if (item.input == 'section') {
    if (!item.id) item.id = nanoID()
    /* section, collapsible */
    if (item.type == 'tab' && item.tabs) {
      item.tabs.forEach(tab => {
        if (!tab.id) tab.id = nanoID()
        if (tab.design) {
          expandWorkDesign(information, tab.design);
        }
      });
    }
    /* section, collapsible */
    else if (item.design) {
      expandWorkDesign(information, item.design);
    }
  } else if (item && item.id) {
    const result = information.find((x) => x.id == item.id);
    if (result) {
      const informationItem = _.cloneDeep(result);
      /* list design */
      if (informationItem.type == 15 && Array.isArray(informationItem.fields) && item.design) {
        expandWorkDesign(informationItem.fields, item.design);
      }
      /* table / list fields */
      if (informationItem.type == 14 || informationItem.type == 15) {
        if (!Array.isArray(informationItem.fields) || informationItem.fields.length === 0) {
          item.fields = [];
        } else if (!Array.isArray(item.fields) || item.fields.length === 0) {
          item.fields = [...informationItem.fields];
        } else {
          /* remove fields */
          item.fields = item.fields
            .filter((x) => informationItem.fields.some((y) => y.id === x.id))
            .map((x) => expandWorkDesignInternal(informationItem.fields, x));
          /* add fields */
          informationItem.fields
            .filter((x) => !item.fields.some((y) => y.id === x.id))
            .forEach((x) =>
              item.fields.splice(informationItem.fields.findIndex((y) => y.id === x.id), 0, x)
            );
        }
      }

      propDowns.forEach(prop => {
        if (
          informationItem[prop] !== null &&
          informationItem[prop] !== undefined &&
          informationItem[prop] !== ''
        ) {
          item[prop] = informationItem[prop];
        }
      });
    }
  }

  return item;
}

var expandWorkDesign = function (information, form) {
  if (form && form.rows != null) {
    form.rows.forEach(row => {
      if (row.items != null) {
        //everything to be included, no need filter
        row.items = row.items.map(y => expandWorkDesignInternal(information, y));
      }
    })
  }
}

const classificationValuesToKeep = [
  // general
  'id', 'hide', 'input', 'label', 'description',
  // controls
  'required', 'readOnly', 'visibilityScript', 'visible', // to-do remove visibilityScript
  'formula', 'requiredFormula', 'readOnlyFormula', 'visibleFormula',
  'addRemoveItems', 'disabledAddRemoveItems', 'due', 'assignee', 'unassignee', 'addAssignee',
  // text
  'qrScan',
  // yes no
  'checkboxLabel',
  // module
  'autoCompleteFormDataFields', 'scriptOnly', 'filter', 'queryScript', 'sort',
  // money
  'prefix',
  // number
  'decimalPlaces',
  // location
  'detectOnly',
  // table, list
  'fields',
  // table
  'columnWidth', 'simpleMode',
  // list
  'displayField',
  // date, date time
  'enableCustomDateFormat', 'customDateFormat', 'customDateFormatId', 'withTime', 'dateValidate',
  // module, image, file
  'allowMultiple',
  // image, file: §4.3.2 GNey auto-fill config — FORM-level element behavior, must survive
  // design-save compaction
  'aiFill',
  // section
  'design',
  // undefined
  'schedule',
  // button
  'eventName',
  'advancedTemplate'
];

var removeInheritInformationValue = function (item) {
  for (var key in item) {
    if (!item.custom) {
      if (!classificationValuesToKeep.some(x => x == key)) {
        delete item[key];
      } else if (item[key] === null) {
        delete item[key];
      }
    }
  }

  /* fields */
  if (item.fields != null) {
    item.fields.forEach(column => {
      removeInheritInformationValue(column);
    });
  } else {
    delete item['fields'];
  }

  /* design */
  if (item.design != null) {
    compactWorkDesignInternal(item.design);
  } else {
    delete item['design'];
  }
}

var compactField = function (item) {
  if (item.input == 'section') {
    if (item.type == 'tab') {
      delete item['design'];
      if (item.tabs != null) {
        item.tabs.forEach(tab => {
          compactWorkDesignInternal(tab.design);
        });
      }
    } else {
      delete item['tabs'];
      if (item.design != null) {
        compactWorkDesignInternal(item.design);
      }
    }
  }

  /* element */
  const elements = ["description", "image", "empty", 'button'];
  /* information item */
  if (item.id && item.fieldName && elements.indexOf(item.input) === -1) {
    removeInheritInformationValue(item);
  }

  //form design cloned item from too;
  return item;
}

var compactWorkDesignInternal = function (design) {
  if (design.rows != null) {
    design.rows.forEach(x => {
      if (x.items != null) {
        x.items = x.items.map(y => compactField(y))
      }
    });
  }
}

var compactClassification = function (canvas) {
  /* remove ribbons */
  if (canvas.ribbons && canvas.ribbons.length == 1) {
    if (canvas.ribbons[0].name == 'Initial') {
      delete canvas.ribbons;
    }
  }
  /* activities */
  if (canvas.activities) {
    canvas.activities.forEach(activity => {
      if (activity.design) {
        delete activity.classification;
        compactWorkDesignInternal(activity.design);
      }
    });
  }
  /* standard activities */
  if (canvas.standardActivities) {
    canvas.standardActivities.forEach(activity => {
      if (activity.design) {
        delete activity.classification;
        compactWorkDesignInternal(activity.design);
      }
    });
  }
}

var classificationInformationInternal = (item) => {
  if (item.items) {
    let result = item.items;
    let items = item.items.flatMap(x => {
      if (x.input == "section" && x.type == "tab" && x.tabs) {
        return x.tabs.flatMap((z) => {
          return z.design.rows.flatMap((y) => {
            return classificationInformationInternal(y);
          });
        });
      } else if (x.input == "section" && x.design) {
        return x.design.rows.flatMap((y) => {
          return classificationInformationInternal(y);
        });
      }
    });
    return result.concat(...items);
  }
  return [];
}

var classificationInformation = (rows, information) => {
  return rows.flatMap(x => classificationInformationInternal(x))
    .map(x => {
      if (x) {
        const item = information.find(y => y.id === x.id);
        if (item) return { ...x, ...item }
      }
    })
    .filter(x => x);
}

var compactMenuDesign = function (canvas, menu) {
  if (menu && menu.list && menu.list.columns) {
    let menuValuesToKeep = ['id']

    for (var column in menu.list.columns) {
      for (var key in column) {
        if (!menuValuesToKeep.some(x => x == key)) {
          delete column[key];
        }
      }
    }
  }
}

var trimMenu = function (menus) {
  if (menus == null)
    return null;

  return menus.map(menu => {
    if (menu.type == 'group') {
      return {
        'id': menu.id,
        'emoji': menu.emoji,
        'name': menu.name,
        'type': menu.type,
        'children': trimMenu(menu.children)
      };
    } else {
      return {
        'id': menu.id,
      };

    }
  });
}

var visibleMenu = (menus) => {
  return Array.isArray(menus) ? menus.filter(x => {
    if (x.type === 'group') {
      if (x.children && x.children.length > 0) {
        x.children = visibleMenu(x.children);
        return Boolean(x.children.length);
      } else {
        return false;
      }
    } else {
      return true;
    }
  }) : [];
}

var expandMenu = function (menus, vectors, studio) {
  menus.forEach(menu => {
    if (menu.type != 'group') {
      var vector = vectors.find(x => x.id == menu.id);
      if (vector) {
        menu.name = vector.name;
        menu.emoji = vector.emoji;
        menu.children = [];
        if (studio) {
          menu.extension = { menus: vector.menus };
        } else {
          menu.children = vector.menus;
        }
      }
    }

    if (Array.isArray(menu.children)) {
      expandMenu(menu.children, vectors, studio);
    } else {
      menu.children = [];
    }
  });
}
/**
 * 
 * @param {Array} menus 
 * @param {Array} previousMenus 
 */
const setOpenedMenus = (menus, previousMenus) => {
  const menuIds = _.reduceDeep(
    previousMenus,
    (acc, value, key, parent, context) => {
      if (value.open && Array.isArray(value.children) && value.children.length) {
        acc.push(value.id);
        return acc;
      }
      context["skipChildren"] = true;
      return acc;
    }, [], { childrenPath: "children" }
  );

  if (menuIds.length) {
    _.eachDeep(menus, (menu) => {
      if (menuIds.includes(menu.id)) {
        menu.open = true;
      }
    }, { childrenPath: "children" });
  }
}

var setChildMenu = function (menus, vector) {
  for (var menu of menus) {
    if (menu.id == vector.id.toString()) {
      let index = menus.indexOf(menu);
      if (index > -1) {
        menus.splice(index, 1, {
          ...menu,
          extension: vector.extension
        });
      }
      return;
    }
    if (Array.isArray(menu.children)) {
      setChildMenu(menu.children, vector);
    }
  }
}

var expandSidebarMenu = function (parentId, menus) {
  let menu = _.findDeep(menus, x => (x.id == parentId), {
    childrenPath: 'children'
  })

  if (menu && menu.value && menu.value.id) {
    menu.value.open = true
    if (menu && menu.parent && menu.parent.id)
      expandSidebarMenu(menu.parent.id, menus)
  }
}

const collapseAllSidebarMenu = (menus) => {
  if (!Array.isArray(menus)) return;

  menus.forEach(menu => {
    menu.open = false;

    if (menu.children && menu.children.length > 0) {
      collapseAllSidebarMenu(menu.children);
    }
  });
}

const getFirstOrDefaultMenu = (menus, moduleId) => {
  for (const menu of menus) {
    if (menu.type === "group") {
      if (menu.children && menu.children.length > 0) {
        const found = getFirstOrDefaultMenu(menu.children);
        if (found) return found;
      }
      continue;
    }

    if (menu.module) {
      if (!moduleId || menu.module == moduleId) {
        let listingId = null;
        if (menu.children && menu.children.length > 0) {
          listingId = menu.children[0].id;
        }
        return {
          moduleId: menu.module,
          listingId: listingId
        }
      }
    }
  }

  return null;
}

const isAnySidebarMenuOpen = (menus) => {
  if (!Array.isArray(menus)) return false;

  return menus.some(menu => 
    typeof menu === 'object' &&
    menu.open === true &&
    Array.isArray(menu.children) &&
    menu.children.length > 0
  );
};

// get visible elements
var getVisibleElements = (elements, form) => {
  if (elements == null) {
    elements = [];
  }

  if (form && form.rows != null) {
    form.rows.forEach(row => {
      if (row.items != null) {
        row.items.forEach(item => {
          getVisibleInternalElements(elements, item);
        });
      }
    });
  }

  return elements;
}
var getVisibleInternalElements = (elements, item) => {
  if (item.visible !== false) {
    if (item.input == 'section') {
      //tab
      if (item.type == 'tab' && item.tabs) {
        item.tabs.forEach(tab => {
          if (tab.design) {
            getVisibleElements(elements, tab.design);
          }
        });
      }
      //design
      else if (item.design) {
        getVisibleElements(elements, item.design);
      }
    } else if (item && item.id) {
      let cloneItem = _.cloneDeep(item);
      if (cloneItem.type == 14 && cloneItem.fields && Array.isArray(cloneItem.fields) && cloneItem.fields.length) {
        cloneItem.fields = cloneItem.fields.filter(x => x.visible !== false && !x.hide);
      } else if (cloneItem.type == 15 && cloneItem.design) {
        /* SS06075: the validators for a list's columns are built from THIS field list, and the
           per-row required check (findListValidatorField) already consults the ROW design's own
           visible/readOnly flags. Filtering the TEMPLATE by visibility here dropped every column
           a visibility formula hides at load (template visible:false) — so a required column that
           the formula later showed on a row never had a validator, and the form submitted with
           it empty. Take the whole template; the row design decides. */
        cloneItem.fields = getAllElements(null, cloneItem.design);
      }
      elements.push(cloneItem);
    }
  }

  return elements;
}
// get validator elements
var getFormValidators = (elements, validatorKey, formData) => {
  if (elements == null) {
    elements = [];
  }
  // filter exclude elements
  const validatorElements = elements.filter(x =>
    ["description", "image", "empty", 'button'].indexOf(x.input) === -1
  );
  // key
  const key = validatorKey || "fieldName";
  // validators
  let validators = {};
  validatorElements.forEach(x => {
    const value = isObject(formData) ? formData[x[key]] : null;
    validators[x[key]] = formValidator(x, value);
  });
  return validators;
}
/**
 * find list validator field
 * @param {Array} designs 
 * @param {Object} item 
 * @param {Object} value 
 * @param {Array} formData 
 * @returns {Object|null}
 */
const findListValidatorField = (designs, item, value, formData) => {
  if (
    !Array.isArray(designs) || 
    !Array.isArray(formData) || 
    !isObject(item) || 
    !isObject(value)
  ) {
    return null;
  }

  const findIndex = formData.findIndex(x => x.id === value.id);
  const design = designs[findIndex];
  if (!design) return null;

  const field = findFormField(design.rows, item);
  if (!field || field.visible === false || field.readOnly) {
    return null;
  }

  return field;
}
// form validator
var formValidator = function (x, formData) {
  let validator = {}
  function boolean(val) {
    return val || false
  }

  if (x.readOnly) {
    return {}
  } else if (x.type == 14) {
    let requiredFields = {}
    if (x.fields && Array.isArray(x.fields) && x.fields.length) {
      x.fields.forEach(y => {
        requiredFields[y.fieldName] = formValidator(y)
      })
      return {
        required: requiredIf(() => x.required),
        $each: requiredFields
      }
    }
  } else if (x.type == 15) {
    const rules = {
      required: requiredIf(() => x.required),
      $each: {}
    }

    if (x.fields && Array.isArray(x.fields) && x.fields.length) {
      x.fields.forEach(y => {
        let validator = {
          required: requiredIf((val) => {
            const field = findListValidatorField(x.designs, y, val, formData);
            return field && field.required ? true : false;
          })
        }

        if (y.type == 1) { // yes/no
          // SS06141: required means CHECKED, as { required, boolean } outside a List. vuelidate's
          // required counts false as a value, so an unticked row slipped through.
          validator.boolean = (val, parent) => {
            const field = findListValidatorField(x.designs, y, parent, formData);
            return field && field.required ? val === true : true;
          }
        } else if (y.type == 14) { // table
          let eachFields = {};
          if (y.fields && Array.isArray(y.fields) && y.fields.length) {
            y.fields.forEach(f => {
              eachFields[f.fieldName] = formValidator(f);
            });
          }

          validator.$each = eachFields;
        } else if (y.type == 22) { // content
          validator.htmlValidator = (val, parent) => {
            if (!val) return true;

            const field = findListValidatorField(x.designs, y, parent, formData);
            if (field && field.required) {
              return htmlValidator(val);
            }

            return true;
          }
        } else if (y.type == 25) { // email
          validator.email = (val, parent) => {
            if (!val) return true;

            const field = findListValidatorField(x.designs, y, parent, formData);
            if (!field) return true;

            return emailValidator(val);
          }
        } else if (y.type == 26) { // phone
          validator.phone = (val, parent) => {
            if (!val) return true;

            const field = findListValidatorField(x.designs, y, parent, formData);
            if (!field) return true;

            return phoneValidator(val);
          }
        } else if (y.type == 33) { // url
          validator.url = (val, parent) => {
            if (!val || val.constructor !== String) return true;

            const field = findListValidatorField(x.designs, y, parent, formData);
            if (!field) return true;

            return urlValidator(val) || uNCValidator(val);
          }
        }

        rules.$each[y.fieldName] = validator;
      });
    }

    return rules;
  } else if (x.required) {
    if (x.type == 1)
      validator = { required, boolean }
    else if (x.type == 22)
      validator = { required, htmlValidator }
    else
      validator = { required }
  }
  /* email */
  if (x.type == 25)
    validator = {
      ...validator,
      email: (val) => val ? emailValidator(val) : true,
    }
  /* phone */
  else if (x.type == 26)
    validator = {
      ...validator,
      phone: (val) => val ? phoneValidator(val) : true,
    }
  /* link */
  else if (x.type == 33)
    validator = {
      ...validator,
      url: (val) => val && val.constructor === String ? urlValidator(val) || uNCValidator(val) : true,
    }
  /* custom validator */
  if (x.validator)
    validator = { ...validator, ...x.validator }

  return validator
}
// form visibility
var formElementVisibility = (x, formData) => {
  if (x.visible != null)
    return x.visible;

  // table / list
  if ((x.type == 14 || x.type == 15) && !x.isParent) {
    if (
      formElementVisibility({ ...x, isParent: true }, formData) &&
      x.fields &&
      Array.isArray(x.fields) &&
      x.fields.length
    )
      return !!(x.fields.filter(y => formElementVisibility(y)).length);
    else
      return false;
  }

  // table field => hide
  if (x.input === 'hidden' || x.hide === true)
    return false;

  // phase out (FTSControlModal)
  if (x.hiddenFunc && formData)
    return !x.hiddenFunc(formData);

  // phase out
  if (x.visibilityScript) {
    let data = formData;
    try {
      return eval(x.visibilityScript);
    } catch (ex) {
      return true;
    }
  }

  return true;
}
/**
 * 
 * @param {element} el 
 * @returns array
 */
 const composedElementPath = (el) => {
  let path = [];
  let element = el;
  while (element) {
    if (el.tagName === 'HTML') {
      path.push(document);
      path.push(window);
      return path;
    }
    path.push(element);
    element = element.parentElement;
  }
  return path;
}
/**
 * 
 * @param {event} evt 
 * @returns array
 */
const composedPath = (evt) => {
  if (evt.composedPath) {
    return evt.composedPath();
  }
  return composedElementPath(evt.target);
}
/**
 * 
 * @param {array} path 
 * @param {array} classList 
 * @returns boolean
 */
const elementPathContainClassName = (path, classList) => {
  for (let i = 0; i < path.length; i++) {
    const element = path[i];
    if (classList.some(x => element.classList && element.classList.contains(x))) {
      return true;
    }
  }
  return false;
}
/**
 * 
 * @param {element} el 
 * @param {string} className 
 * @returns boolean
 */
const elementContainClassName = (el, classList) =>
  elementPathContainClassName(composedElementPath(el), classList.split(' '));
/**
 * 
 * @param {event} evt 
 * @param {string} classList 
 * @returns boolean
 */
const eventContainClassName = (evt, classList) =>
  elementPathContainClassName(evt.path || composedPath(evt), classList.split(' '));
/**
 * 
 * @param {object} item (optional) 
 * @returns boolean
 */
var elementStopPropagation = function ($event) {
  const evt = $event || event;
  const path = evt.path || composedPath(evt);
  return elementPathContainClassName(path, ['stopPropagation']);
}
/**
 * 
 * @param {ClipboardEvent} event - The paste event from the input field.
 * @param {Function} valueSetter - A function to update the input value.
 */
const sanitizePastedText = (event, valueSetter) => {
  event.preventDefault();

  let clipboardData = event.clipboardData || window.clipboardData;
  if (!clipboardData) return;

  let pastedText = clipboardData.getData("text");
  pastedText = pastedText.replace(/[\t\n]/g, " ").replace(/\r/g, "");

  let input = event.target;
  let start = input.selectionStart;
  let end = input.selectionEnd;
  let currentValue = input.value;
  let newValue = currentValue.substring(0, start) + pastedText + currentValue.substring(end);

  valueSetter(newValue);

  requestAnimationFrame(() => {
    input.selectionStart = input.selectionEnd = start + pastedText.length;
    // dispatch 'input' event
    const inputEvent = new Event('input', { bubbles: true });
    input.dispatchEvent(inputEvent);
  });
}

const studioRoute = (payload) => {
  let route = { "name": "studio" };
  if (payload.workspaceId) {
    route.query = { "wsId": encodeids(payload.workspaceId) };
  } else if (payload.moduleId) {
    route.query = { "id": encodeids(payload.moduleId) };
    if (payload.type && payload.typeId) {
      route.query.t = payload.type;
      route.query.tid = payload.typeId;
    }
  }
  return route;
}

var clientListViewRoute = function (payload) {
  let route = {
    name: 'list-view',
    params: { moduleId: encodeids(payload.moduleId) },
    query: {}
  }
  /* list id */
  if (payload.listId)
    route.query.l = payload.listId;
  /* entry id */
  if (
    payload.id &&
    (
      !payload.activity ||
      (
        payload.activity && payload.activity.toLowerCase != 'create'
      )
    )
  )
    route.query.id = encodeids(payload.id);
  /* activity id */
  if (payload.activity)
    route.query.a = payload.activity;

  return route;
}

var clientPathUrl = function (payload) {
  let url = `/#/${payload?.isLite? 'lite' : 'list' }/${encodeids(payload.moduleId)}`
  // list id
  if (payload.listId) {
    url += `?l=${payload.listId}`
  }
  // locus id
  if (
    payload.id &&
    (!payload.activity || (payload.activity && payload.activity.toLowerCase != 'create'))
  ) {
    if (payload.listId)
      url += `&id=${queryEncodeids(payload.moduleId, payload.id)}`
    else
      url += `?id=${queryEncodeids(payload.moduleId, payload.id)}`
  }
  // activity id
  if (payload.activity) {
    if (payload.listId || payload.id)
      url += `&a=${payload.activity}`
    else
      url += `?a=${payload.activity}`
  }
  return url
}

var imageLoader = function (src, success, error) {
  const img = new Image();
  img.onload = success;
  img.onerror = error;
  img.src = src;
}

function findElementById(elements, changeId, eleInput, changeIndex) {
  return elements.reduce((x, item) => {
    const input = item.input || eleInput

    if (x) {
      return x;
    }
    if (item.id === changeId) {
      return item;
    }
    if (item.type == 15 && changeIndex != null) {
      if (Array.isArray(item.designs)) {
        let design = item.designs[changeIndex || 0];

        if (design && Array.isArray(design.rows)) {
          return findElementById(design.rows, changeId, null, changeIndex);
        }
      }

      return null;
    }
    if (item.fields && item.fields.some(y => y['id'] == changeId)) {
      return item.fields.find(y => y['id'] == changeId);
    }
    if (input === 'section' && item.type === 'tab' && item.tabs && item.tabs.length)
      return findElementById(item.tabs, changeId, input, changeIndex)
    if (input === 'section' && item.design && item.design.rows && item.design.rows.length)
      return findElementById(item.design.rows, changeId, input, changeIndex)
    if (item.items)
      return findElementById(item.items, changeId, input, changeIndex)
  }, null)
}

const findFormField = (fields, field, formData) => {
  const { id: fieldId, index: fieldIndex, input: fieldInput } = field;

  for (let item of fields) {
    const input = item.input || fieldInput;

    if (item.id === fieldId) return item;
    // item.type 15
    if (item.type == 15 && fieldIndex != null) {
      if (!Array.isArray(item.designs)) {
        const value = formData[item.fieldName];
        Vue.set(item, "designs", []);

        if (Array.isArray(value)) {
          item.designs = value.map(() => _.cloneDeep(item.design));
        }
      }

      let design = item.designs[fieldIndex || 0];
      if (design && design.rows) {
        let found = findFormField(design.rows, field, formData);
        if (found) return found;
      }
    }
    // item.type 14
    if (item.type == 14 && item.fields) {
      let found = item.fields.find(f => f.id == fieldId);
      if (found) return found;
    }
    // section:tab
    if (input === "section" && item.type === "tab" && item.tabs && item.tabs.length) {
      const found = findFormField(item.tabs, { ...field, input }, formData);
      if (found) return found;
    }
    // section
    if (input === "section" && item.design && item.design.rows && item.design.rows.length) {
      const found = findFormField(item.design.rows, { ...field, input }, formData);
      if (found) return found;
    }

    if (item.items) {
      const found = findFormField(item.items, { ...field, input }, formData);
      if (found) return found;
    }
  }
}

/* The current user's profile fields a formula may read (server ruling 2026-10-31, mirrored by
   CurrentUserFields.Names): {{CURRENTUSER.DisplayName}} etc. The server loads the profile only
   when a formula uses one; {{CURRENTUSER}} alone stays the username. */
const currentUserFields = [
  { name: "Display Name", fieldName: "DisplayName", type: 0 },
  { name: "Email", fieldName: "Email", type: 0 },
  { name: "First Name", fieldName: "FirstName", type: 0 },
  { name: "Last Name", fieldName: "LastName", type: 0 },
  { name: "Phone Number", fieldName: "PhoneNumber", type: 0 },
];

/* {{query.code}} / {{event.items}} (automation-blocks-simplification P9, mirrored by the server's
   VariableConverter.PayloadRoot): a link's query and an event's payload have no fields of their own, so
   the segments after them are free keys, read blank when missing. True once the walk has resolved
   only that payload. */
const lenientPayloads = ["query", "event"];
const isLenientPayloadChip = (resolved) =>
  resolved.length == 1 && !!resolved[0] && lenientPayloads.includes(resolved[0].fieldName) && !resolved[0].moduleId;

const formulaConstants = [
  {
    name: "CURRENT USER",
    fieldName: "CURRENTUSER",
    type: 19,
    // a saved {{CURRENTUSER.DisplayName}} chip resolves its second segment here
    constantFields: currentUserFields
  },
  // one menu row per field: picking it inserts the whole {{CURRENTUSER.<field>}} chip
  ...currentUserFields.map(field => ({
    name: `CURRENT USER's ${field.name}`,
    fieldName: `CURRENTUSER.${field.fieldName}`,
    type: field.type
  })),
  {
    name: "TODAY",
    fieldName: "TODAY",
    type: 5
  },
  {
    name: "NOW",
    fieldName: "NOW",
    type: 5
  },
];

const formulaTemplates = [
  // D5 (excel-formula-editor-proposal §3.5b): one spelling per concept — the IF/AND/OR/
  // ISNOTBLANK template rows are GONE; the function-hint ledger owns those concepts and
  // inserts the canonical Excel spellings (§3.7b). What remains here are the tokenizer
  // patterns with no function twin yet, kept until the unified menu absorbs the # trigger.
  {
    name: 'condition',
    template: '<fts-i-unit contenteditable="false">value</fts-i-unit> == <fts-i-unit contenteditable="false">target</fts-i-unit> '
  },
  // "is set" for an entry/user reference (and any other field): hasValue is exactly the
  // "Customer.Id > 0" test -- the backend delegate is `FETIASEntity -> Id > 0` -- but reads as a
  // question instead of an id comparison, and unlike the raw .Id it is a whitelisted native token
  // so it can also compute on the device instead of forcing a sync.
  {
    name: 'is set (has value)',
    template: 'hasValue(<fts-i-unit contenteditable="false">field</fts-i-unit>)'
  },
  {
    name: 'sum (list / table)',
    template: 'sum(<fts-i-unit contenteditable="false">#Table\'s #Column</fts-i-unit>)'
  },
  {
    name: 'filter (list / table)',
    template: 'filter(<fts-i-unit contenteditable="false">#Table\'s #Column</fts-i-unit> == <fts-i-unit contenteditable="false">value</fts-i-unit>)'
  },
  {
    name: 'find (list / table)',
    template: 'find(<fts-i-unit contenteditable="false">#Table\'s #Column</fts-i-unit> == <fts-i-unit contenteditable="false">value</fts-i-unit>)'
  },
  {
    name: 'Row Auto Number',
    template: 'rowAutoNumber'
  },
  {
    name: 'addRow (table)',
    template: 'addRow(<fts-i-unit contenteditable="false">#Table</fts-i-unit>)'
  },
  {
    name: 'removeRow (table)',
    template: 'removeRow(<fts-i-unit contenteditable="false">#Table</fts-i-unit>, <fts-i-unit contenteditable="false">row</fts-i-unit>)'
  },
  {
    name: 'sort (list / table)',
    template: 'sort(<fts-i-unit contenteditable="false">#Table\'s #Column</fts-i-unit>, <fts-i-unit contenteditable="false">\'ascending\' \\ \'descending\'</fts-i-unit>)'
  },
  {
    name: 'group (list / table)',
    template: 'group(<fts-i-unit contenteditable="false">#Information</fts-i-unit>)'
  },
  // {
  //   name: 'sort items',
  //   template: 'sort(#Table.#Columns)'
  // },
  // {
  //   name: 'sort items',
  //   template: 'sort(#Table.#Columns)'
  // },
];

/* Computed-field formula autocomplete (Information design modal, formula mode).
   A computed field may only use the bounded native subset the backend's
   NativeBundleBuilder.IsClientComputable accepts and nativeFormula.js evaluates on-device —
   so this is a SEPARATE, narrower list, kept apart from formulaConstants/formulaTemplates which
   the automation / action / query editors still use in full. Anything offered here round-trips
   as a valid computed field instead of bouncing off the backend "not client-computable" gate.
   `columnOnly` marks a token meaningful only inside a table-column formula (rowAutoNumber numbers
   the table's rows; at top-level field level it is a no-op), surfaced only in column context. */
const computedFormulaConstants = [
  // CURRENT USER is intentionally omitted — CurrentUser is not client-computable.
  {
    name: "TODAY",
    fieldName: "TODAY",
    type: 5
  },
  {
    name: "NOW",
    fieldName: "NOW",
    type: 5
  },
  // Row Auto Number: a constant (so it renders as a chip like TODAY/NOW) rather than a raw-text
  // template. Inserts {{ROWAUTONUMBER}}, which the backend converter normalizes to the native
  // rowNumber() (the row's 1-based position). columnOnly: meaningful only inside a table column.
  {
    name: "Row Auto Number",
    fieldName: "ROWAUTONUMBER",
    type: 2,
    columnOnly: true
  },
];

const computedFormulaTemplates = [
  // Inline conditional as iif(condition, a, b) -- NOT the Python ternary `a if c else b`. The native
  // converter (applyNativeTransforms) does not translate the ternary, so a computed field authored
  // with `if ... else ...` is rejected at save by the client-computability gate; iif() is a first-class
  // native helper (whitelisted, client-computable) and round-trips. See formula-scripting-clean-cut §7.
  //
  // DELIBERATELY NOT re-spelled to the Excel names yet (excel-formula-editor-proposal §3.5):
  // a computed field may only use what the DEVICE computes, and IF/AND/OR are server-tier until
  // Phase 3 (IF is truthiness, client iif takes a real boolean — not byte-safe to alias).
  //
  // D5 (excel-formula-editor-proposal §3.5b): the iif and ISNOTBLANK template rows died —
  // the function-hint ledger owns those concepts and offers the computed-eligible client
  // tier truthfully (iif surfaces there while its twin IF is server-tier). The operator
  // patterns below stay: `or`/`and` OPERATORS are the client-computable spelling while the
  // AND()/OR() functions are server-tier — no function twin is available in computed mode.
  {
    name: 'or',
    template: '<fts-i-unit contenteditable="false">condition 1</fts-i-unit> or <fts-i-unit contenteditable="false">condition 2</fts-i-unit> '
  },
  {
    name: 'and',
    template: '<fts-i-unit contenteditable="false">condition 1</fts-i-unit> and <fts-i-unit contenteditable="false">condition 2</fts-i-unit> '
  },
  {
    name: 'condition',
    template: '<fts-i-unit contenteditable="false">value</fts-i-unit> == <fts-i-unit contenteditable="false">target</fts-i-unit> '
  },
  // "is set": client-computable (hasValue is in the v1 whitelist), so a computed field written
  // this way stays on-device -- where the equivalent `Customer.Id > 0` is rejected at save.
  {
    name: 'is set (has value)',
    template: 'hasValue(<fts-i-unit contenteditable="false">field</fts-i-unit>)'
  },
  {
    name: 'sum (list / table)',
    template: 'sum(<fts-i-unit contenteditable="false">#Table\'s #Column</fts-i-unit>)'
  },
  // Row Auto Number moved to computedFormulaConstants (a chip), so it is no longer a raw-text template.
];

const externalMetaTitle = (payload) => {
  let title = '';
  /* activity name */
  if (payload.activityId !== 'view') {
    title = payload.activityName;
  }
  /* document id */
  if (payload.documentId && payload.activityId !== 'create') {
    title += ` ${payload.documentId} in`;
  }
  /* return  */
  return `${title.trim()} ${payload.moduleName} for ${payload.workspaceName} | Inistate`;
}

const getFormAvailableCustoms = (list, entries) =>
  list.filter(x => {
    if (x.condition) {
      try {
        const items = entries;
        return Boolean(eval(x.condition))
      } catch(err) {
        console.error('Error: Custom condition', err);
        return false;
      }
    }
    return true;
  });

const executeEntryCustom = async (payload) => {
  let action = {...payload.action};
  let items = [...payload.items];
  let modal = payload.modal;
  if (builderMode() && !Boolean(action && action.type === 'customPage')) {
    Vue.toasted.info(builderNotAvailableMessage(action && action.name || 'Custom'));
    return;
  }
  
  const replaceURL = (action) => {
    if (action.url.includes('{id}')) {
      action.url = action.url.replaceAll('{id}', items[0].id);
    }
  }

  try {
    if (action.type === 'customPage') {
      action.url && replaceURL(action);
      const res = await modal.show(action); 
      if (res && res.url) {
        window.location.assign(res.url);
      }
    } else if (action.type === 'goToUrl') {
      if (action.url) {
        replaceURL(action);
        action.newTab ? window.open(action.url) : window.open(action.url, '_self');
      }
    } else if (action.script) {
      if (action.script.includes('{refreshToken}')) {
        const refreshToken = LocalStorageService.getRefreshToken();
        action.script = action.script.replaceAll('{refreshToken}', refreshToken);
      }

      eval(action.script);
    }
  } catch(err) { console.error(err); }
}

const entryCopyLink = (payload = {}) => {
  const {
    moduleName,
    activityId,
    documentId,
    title
  } = payload;

  try {
    // create range
    const range = document.createRange();
    range.selectNode(document.body);
    // create node
    const newNode = document.createElement("a");
    newNode.setAttribute("href", window.location.href);
    // set the inner text
    if (activityId === "create") {
      newNode.innerText = `New | ${moduleName || "Inistate"}`;
    } else if (documentId || title) {
      newNode.innerText = (`${documentId || ""} ${title || ""}`).trim();
    } else {
      newNode.innerText = "Inistate | One Step Further";
    }
    // inset node
    range.insertNode(newNode);
    range.selectNode(newNode);
    // selection
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    // copy
    document.execCommand("copy");
    // destory node
    document.children[0].removeChild(newNode);

    return { message: "Copied." };
  } catch(err) {
    console.warn(err);
    throw { message: "Failed to copy link.", error: err };
  }
}

const builderMode = () => {
  try {
    return window !== window.parent && window.parent.appName === 'inistate-builder';
  } catch(err) {
    return false;
  }
};

const getModulesFromWorkspaceMenus = (menus) => {
  return menus.reduce((accumulator, currentValue) => {
    if (currentValue.type === 'group') {
      accumulator = accumulator.concat(...getModulesFromWorkspaceMenus(currentValue.children));
    } else if (currentValue.module) {
      accumulator.push({...currentValue, id: currentValue.id && currentValue.id.toString()});
    }
    return accumulator;
  }, []);
}

const getWorkspaceShortName = (workspaceName) => workspaceName ? workspaceName.trim().substring(0, 2) : "...";

const translateCommentContent = (content, users = []) => {
  let commentContent = content;
  try {
    const matches = commentMentionsValidator(commentContent);
    if (matches) {
      const mentions = [...new Set (matches)];
      let result = [];
      for (let i = 0; i < mentions.length; i++) {
        const user = users.find(x => x.username === mentions[i].substring(1));
        if (user && user.displayName)
          result.push({ username: mentions[i], displayName: user.displayName});
      }
      if (result.length) {
        for (let i = 0; i < result.length; i++) {
          commentContent = commentContent.replaceAll(result[i].username, `<span class="history-content-mention">@${result[i].displayName.trim()}</span>`);
        }
      }
    }
  } catch(err) {
    console.error(err);
  }
  return commentContent;
}

const redirectMobileView = ({ appUrl, baseUrl, accessToken, refreshToken, webData, workspaceId } = {}) => {
  LocalStorageService.setWebData(webAppConfig.baseServer, { ...webData, prefDevice: DeviceName.WEBAPP });

  if (appUrl && baseUrl && accessToken && refreshToken) {
    let redirectUrl = `${appUrl}/?accessToken=${accessToken}&refreshToken=${refreshToken}&origin=${baseUrl}`;

    if (workspaceId) {
      redirectUrl += `&wsId=${workspaceId}`;
    }

    window.location.replace(redirectUrl);
    return true;
  } else {
    console.warn("Redirection aborted due to missing parameters.");
    return false;
  }
}
/**
 * 
 * @param {Object} router 
 * @param {Object} route 
 * @param {Array<{ name: string, value: any }>} params 
 * @param {boolean} replace 
 */
const addDesignQueryParams = (router, route, params, replace) => {
  if (!route || route.name !== "studio" || !Array.isArray(params)) return;

  let newQuery = {...route.query};

  params.forEach(({ name, value }) => {
    if (value !== undefined && value !== null) {
      newQuery[name] = value;
    }
  });
  
  if (replace) {
    router.replace({ query: newQuery });
  } else {
    router.push({ query: newQuery });
  }
}
/**
 * 
 * @param {Object} router 
 * @param {Object} route 
 * @param {Array<string>} params 
 * @param {boolean} replace 
 */
const removeDesignQueryParams = (router, route, params, replace) => {
  if (!route || route.name !== "studio" || !Array.isArray(params)) return;
  
  let newQuery = { ...route.query };
  let modified = false;

  params.forEach(param => {
    if (param in newQuery) {
      delete newQuery[param];
      modified = true
    }
  });

  if (!modified) return;

  if (replace) {
    router.replace({ query: newQuery });
  } else {
    router.push({ query: newQuery });
  }
}
/**
 * 
 * @param {Array} users 
 * @param {string} username 
 * @returns {Object|null}
 */
const findUserByUsername = (users, username) => {
  const lowerUsername = username.toLowerCase();
  return users.find(user => {
    const userName = user.userName || user.username;
    return userName.toLowerCase() === lowerUsername;
  });
}
/* crisp chat */
const initCrispChat = () => {
  try {
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = "0426107d-60ad-44c6-a8f5-112986d3e312";
    (function(){
      var d = document;
      var s = d.createElement("script");
      s.src = "https://client.crisp.chat/l.js";
      s.async = 1;
      d.getElementsByTagName("head")[0].appendChild(s);
    })();
    window.$crisp.push(["safe", true]);
  } catch(err) { console.error(err); }
}
const showCrispChat = () => {
  if (window.$crisp) {
    window.$crisp.push(['do', 'chat:show']);
  } else {
    initCrispChat();
  }
}
const hideCrispChat = () => {
  window.$crisp && window.$crisp.push(["do", "chat:hide"]);
}

export default {
  expandWorkDesignInternal: expandWorkDesignInternal,
  findElementById: findElementById,
  findFormField: findFormField,
  model: model,
  defaultProfileImage: defaultProfileImage,
  defaultImage: defaultImage,
  fetiasLogo: fetiasLogo,
  iOSAppStoreURL: iOSAppStoreURL,
  androindPlayStoreURL: androindPlayStoreURL,
  websiteURL: websiteURL,
  termsURL: termsURL,
  privacyPolicyURL: privacyPolicyURL,
  communityURL: communityURL,
  youtubeURL: youtubeURL,
  facebookURL: facebookURL,
  twitterURL: twitterURL,
  linkedinURL: linkedinURL,
  discordURL: discordURL,
  blogURL: blogURL,
  newUUID: newUUID,
  describeTeammateProgress: describeTeammateProgress,
  nanoID: nanoID,
  randomInt: randomInt,
  encodeids: encodeids,
  formatBadgeCount: formatBadgeCount,
  decodeids: decodeids,
  queryEncodeids: queryEncodeids,
  queryDecodeids: queryDecodeids,
  standardActivityIcon: standardActivityIcon,
  informationIcon: informationIcon,
  informationIconHtml: informationIconHtml,
  moduleEmoji: moduleEmoji,
  expandInformationType: expandInformationType,
  expandConnectorInformationType: expandConnectorInformationType,
  getInformationIcon: getInformationIcon,
  informationTypes: informationTypes,
  standards: standards,
  standardActivities: standardActivities,
  adminFeatures: adminFeatures,
  camelize: camelize,
  getDisplayName: displayName,
  getFileColor: fileColor,
  getFileExtension: fileExtension,
  getFilePath: filePath,
  selectOptionBackground: selectOptionBackground,
  momentRelativeTime: momentRelativeTime,
  calendarRelativeTime: calendarRelativeTime,
  dateFormats: dateFormats,
  dateFormatTranslate: dateFormatTranslate,
  formatNumber: formatNumber,
  dragOptions: dragOptions,
  compactClassification: compactClassification,
  classificationInformation: classificationInformation,
  expandWorkDesign: expandWorkDesign,
  compactMenuDesign: compactMenuDesign,
  taskDueDateShortcuts: taskDueDateShortcuts,
  trimMenu: trimMenu,
  visibleMenu: visibleMenu,
  expandMenu: expandMenu,
  setOpenedMenus: setOpenedMenus,
  setChildMenu: setChildMenu,
  expandSidebarMenu: expandSidebarMenu,
  collapseAllSidebarMenu: collapseAllSidebarMenu,
  getFirstOrDefaultMenu: getFirstOrDefaultMenu,
  isAnySidebarMenuOpen: isAnySidebarMenuOpen,
  imageTypes: imageTypes,
  unsupportedImageTypes: unsupportedImageTypes,
  isImageFile: isImageFile,
  isInteger: isInteger,
  isNumber: isNumber,
  isPossibleNumber: isPossibleNumber,
  isArray: isArray,
  isObject: isObject,
  isDateISO_8601: isDateISO_8601,
  isMobileDevice: isMobileDevice,
  getVisibleElements: getVisibleElements,
  getFormValidators: getFormValidators,
  formElementVisibility: formElementVisibility,
  composedElementPath: composedElementPath,
  composedPath: composedPath,
  elementContainClassName: elementContainClassName,
  eventContainClassName: eventContainClassName,
  elementStopPropagation: elementStopPropagation,
  sanitizePastedText: sanitizePastedText,
  getAllElements: getAllElements,
  generateFieldName: generateFieldName,
  studioRoute: studioRoute,
  clientListViewRoute: clientListViewRoute,
  clientPathUrl: clientPathUrl,
  imageLoader: imageLoader,
  formulaConstants: formulaConstants,
  isLenientPayloadChip: isLenientPayloadChip,
  formulaTemplates: formulaTemplates,
  computedFormulaConstants: computedFormulaConstants,
  computedFormulaTemplates: computedFormulaTemplates,
  externalMetaTitle: externalMetaTitle,
  getFormAvailableCustoms: getFormAvailableCustoms,
  executeEntryCustom: executeEntryCustom,
  entryCopyLink: entryCopyLink,
  showCrispChat: showCrispChat,
  hideCrispChat: hideCrispChat,
  builderMode: builderMode(),
  getModulesFromWorkspaceMenus: getModulesFromWorkspaceMenus,
  getWorkspaceShortName: getWorkspaceShortName,
  translateCommentContent: translateCommentContent,
  redirectMobileView: redirectMobileView,
  addDesignQueryParams: addDesignQueryParams,
  removeDesignQueryParams: removeDesignQueryParams,
  findUserByUsername: findUserByUsername
}